// Schedule.js (readonly-capable)
import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Modal, Form, Alert, Table, Spinner, Badge, ProgressBar } from 'react-bootstrap';
import { FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaDownload, FaTree, FaUser, FaHandsHelping, FaFileAlt } from 'react-icons/fa';

import Swal from 'sweetalert2';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

const Schedule = ({ isEditable = true }) => {
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showTreeModal, setShowTreeModal] = useState(false);
  const [showPlantingMethodModal, setShowPlantingMethodModal] = useState(false);
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [showMockTestAlert, setShowMockTestAlert] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [status, setStatus] = useState('completed');
  const [selectedDate, setSelectedDate] = useState(null);
  const [scheduleDates, setScheduleDates] = useState([]);
  const [completedClasses, setCompletedClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [hasShownTreeModal, setHasShownTreeModal] = useState(false);
  const [plantingMethod, setPlantingMethod] = useState(null);
  const [hasPlantationCompleted, setHasPlantationCompleted] = useState(false);
  const [treeStatsChecked, setTreeStatsChecked] = useState(false);
  const [mockTestResults, setMockTestResults] = useState([]);
  const [hasTakenMockTest, setHasTakenMockTest] = useState(false);
  const fileInputRef = useRef(null);
  const certificateRef = useRef(null);
  const [localCompleted, setLocalCompleted] = useState([]);

  const { user, viewMode, getToken } = useAuth();
  const { bearerAuth } = useApi();

  // Calculate progress percentage
  const calculateProgress = () => {
    if (scheduleDates.length === 0) return 0;
    const totalClasses = scheduleDates.length;
    const completedCount = localCompleted.length;
    return Math.round((completedCount / totalClasses) * 100);
  };

  // Get progress color based on percentage
  const getProgressColor = () => {
    const progress = calculateProgress();
    if (progress < 30) return 'danger';
    if (progress < 70) return 'warning';
    if (progress < 100) return 'info';
    return 'success';
  };

  // Get progress text based on percentage
  const getProgressText = () => {
    const progress = calculateProgress();
    const completed = localCompleted.length;
    const total = scheduleDates.length;
    
    if (progress === 0) return 'No classes completed yet';
    if (progress === 100) return `All ${total} classes completed! 🎉`;
    if (progress < 50) return `${completed}/${total} classes completed - Keep going!`;
    if (progress < 80) return `${completed}/${total} classes completed - You're doing great!`;
    return `${completed}/${total} classes completed - Almost there!`;
  };

  // Check if user is eligible for certificate
  const isEligibleForCertificate = () => {
    const allClassesCompleted = completedClasses.length === scheduleDates.length && scheduleDates.length > 0;
    const hasMockTestResults = mockTestResults.length > 0;
    return allClassesCompleted && hasMockTestResults;
  };

  const hasCompletedAllClasses = completedClasses.length === scheduleDates.length && scheduleDates.length > 0;

  // small helper toast
  const showToast = (title, message, icon = 'success') => {
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2800,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });

    Toast.fire({
      icon,
      title,
      text: message
    });
  };

  // If action attempted by school (read-only), show info and block.
  const showReadOnlyBlocked = () => {
    showToast('Read-only', 'School users can only view schedule. Action is disabled.', 'info');
  };

  const fetchTreeStats = async () => {
    try {
      const response = await bearerAuth('/tree-stats', 'GET', { user_id: user?.id });
      const totalTrees = Number(response?.data?.total_trees || 0);
      const completed = Boolean(response?.data?.status) && totalTrees > 0;
      setHasPlantationCompleted(completed);
      setTreeStatsChecked(true);
      return completed;
    } catch (err) {
      console.error('tree-stats error:', err);
      return false;
    }
  };

  const fetchUserData = async () => {
    try {
      setInitialLoading(true);
      setError(null);

      setUserData(user);
      generateSchedule(user);
      console.log('Generated schedule user:', user);
      if (user?.CourseId) {
        await fetchCompletedClasses(user.CourseId);
      }
      await fetchMockTestResults();
      await fetchTreeStats();
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(err.message || 'Failed to load user data');
      showToast('Error', err.message || 'Failed to load user data', 'error');
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchCompletedClasses = async (courseId) => {
    try {
      if (!courseId) {
        setCompletedClasses([]);
        setLocalCompleted([]);
        return;
      }
      const response = await bearerAuth(`student-progress?course_id=${courseId}`);

      if (response.data?.status) {
        const classesData = response.data.data || [];
        const formattedClasses = classesData.map(c => ({
          ...c,
          date: c.date ? c.date.split('T')[0] : c.date
        }));
        setCompletedClasses(formattedClasses);
        setLocalCompleted(formattedClasses);

        // If all classes completed and we haven't shown modal yet
        if (scheduleDates.length > 0 && formattedClasses.length === scheduleDates.length && !hasShownTreeModal) {
          setShowTreeModal(true);
          setHasShownTreeModal(true);
        }
      }
    } catch (err) {
      console.error('Error fetching completed classes:', err);
      showToast('Error', 'Failed to load completed classes', 'error');
    }
  };

  // Fetch mock test results
  const fetchMockTestResults = async () => {
    try {
      const response = await bearerAuth(`mock-tests-results?user_id=${user.id}`);
      
      if (response.data?.status) {
        const results = response.data.data || [];
        setMockTestResults(results);
        setHasTakenMockTest(results.length > 0);
        const treeContribution = user?.tree_contribution || null;
        if (treeContribution?.opted_in) {
          setHasPlantationCompleted(true);
          setPlantingMethod(treeContribution.planting_method || null);
        }
      }
    } catch (err) {
      console.error('Error fetching mock test results:', err);
      // Don't show error toast for this as it's not critical for main functionality
    }
  };

  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const generateSchedule = (user) => {
    if (!user?.admin_exact_start_at || !user?.duration) {
      setScheduleDates([]);
      return;
    }

    const startDate = new Date(user.admin_exact_start_at);
    if (isNaN(startDate.getTime())) {
      setError('Invalid start date format');
      return;
    }

    const dates = [];
    let currentDate = new Date(startDate);
    let daysAdded = 0;
    const includeWeekends = user.inc_sat_sun === 1;

    while (daysAdded < Number(user.duration || 0)) {
      const dayOfWeek = currentDate.getDay();
      if (includeWeekends || (dayOfWeek !== 0 && dayOfWeek !== 6)) {
        dates.push(new Date(currentDate));
        daysAdded++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setScheduleDates(dates);
  };

  const getCompletionDate = () => {
    if (!userData?.admin_exact_start_at || !userData?.duration) return new Date();
    const startDate = new Date(userData.admin_exact_start_at);
    const completionDate = new Date(startDate);
    let daysAdded = 0;
    const includeWeekends = userData.inc_sat_sun === 1;
    while (daysAdded < Number(userData.duration || 0)) {
      completionDate.setDate(completionDate.getDate() + 1);
      const dayOfWeek = completionDate.getDay();
      if (includeWeekends || (dayOfWeek !== 0 && dayOfWeek !== 6)) {
        daysAdded++;
      }
    }
    return completionDate;
  };

  const formatDate = (date) => {
    if (!date) return 'Invalid date';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return 'Invalid date';
    return d.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  };

  const isCompleted = (date) => {
    try {
      const inputDateStr = date instanceof Date ? date.toISOString().split('T')[0] : new Date(date).toISOString().split('T')[0];
      return localCompleted.some((c) => c.date === inputDateStr) || completedClasses.some((c) => c.date === inputDateStr);
    } catch (err) {
      console.error('isCompleted error:', err);
      return false;
    }
  };

  const isEligibleForCompletion = (date) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const dateOnly = new Date(date); dateOnly.setHours(0, 0, 0, 0);
    if (dateOnly >= today) return false;
    return !isCompleted(date);
  };

  // Handle certificate button click with validation
  const handleCertificateClick = () => {
    if (isEligibleForCertificate()) {
      setShowCertificateModal(true);
    } else {
      setShowMockTestAlert(true);
    }
  };

  // When teacher/student tries to mark complete: block if school view
  const handleCompleteClass = (date) => {
    if (!isEditable || viewMode === 'school') {
      showReadOnlyBlocked();
      return;
    }
    setSelectedDate(date);
    setShowCompletionModal(true);
    setRemarks('');
    setStatus('completed');
    setRating(0);
    setApiError(null);
  };

  // Submit completion to API (guarded)
  const handleSubmitCompletion = async () => {
    if (!isEditable || viewMode === 'school') {
      showReadOnlyBlocked();
      return;
    }

    try {
      setLoading(true);
      setApiError(null);
      if (!selectedDate || !userData) throw new Error('Missing data to submit');

      const formattedDate = new Date(selectedDate).toISOString().split('T')[0];
      const progressData = {
        course_id: userData.CourseId,
        date: formattedDate,
        status,
        star: rating,
        remarks: remarks || "Class completed"
      };

      const response = await bearerAuth(`/student-progress`, "POST", progressData);
      if (response.data?.status === true) {
        const newCompleted = { date: formattedDate, status, star: rating, remarks: remarks || 'Class completed' };
        setLocalCompleted(prev => [...prev, newCompleted]);
        setShowCompletionModal(false);
        setRating(0);
        setRemarks('');
        setSelectedDate(null);
        showToast('Success', response.data.message || 'Class marked as completed!');
        await fetchCompletedClasses(userData.CourseId);
      } else {
        throw new Error(response.data?.message || 'Failed to submit completion');
      }
    } catch (err) {
      console.error('submit completion error:', err);
      setApiError(err.response?.data?.message || err.message || 'Submission failed');
      showToast('Error', err.message || 'Failed to mark class as completed', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Tree opt-in: block for school
  const handleTreePlantationOptIn = async () => {
    if (!isEditable || viewMode === 'school') {
      showReadOnlyBlocked();
      return;
    }

    const isAlreadyCompleted = await fetchTreeStats();
    if (isAlreadyCompleted) {
      showToast('Completed', 'Plantation already completed.', 'info');
      return;
    }
    
    // Show planting method selection modal instead of directly opting in
    setShowTreeModal(false);
    setShowPlantingMethodModal(true);
  };

  // Handle planting method selection
  const handlePlantingMethodSelect = async (method) => {
    if (!isEditable || viewMode === 'school') {
      showReadOnlyBlocked();
      return;
    }

    try {
      setLoading(true);
      setPlantingMethod(method);
      
      const userId = {
        user_id: user.id,
        planting_method: method
      };

      const response = await bearerAuth(`/opt-in-tree`, "POST", userId);

      if (response.data?.status) {
        setShowPlantingMethodModal(false);
        
        if (method === 'self') {
          const isAlreadyCompleted = await fetchTreeStats();
          if (isAlreadyCompleted) {
            showToast('Completed', 'Plantation already completed.', 'info');
            return;
          }
          // If user selects self, show photo upload modal
          showToast('Success', response.data.message || 'Plantation option saved.');
          setShowPhotoUploadModal(true);
        } else {
          setHasPlantationCompleted(true);
          Swal.fire({
            icon: 'success',
            title: 'Plantation completed',
            text: 'You can go over product and services page to explore there.',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: 'Products',
            denyButtonText: 'Services',
            cancelButtonText: 'Later',
          }).then((result) => {
            if (result.isConfirmed) {
              window.location.href = '/product';
            } else if (result.isDenied) {
              window.location.href = '/services';
            }
          });
        }
      } else {
        throw new Error(response.data?.message || 'Failed to opt-in');
      }
    } catch (err) {
      console.error('Tree opt-in error:', err);
      showToast('Error', err.message || 'Failed to opt-in', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      setApiError('No file selected');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setSelectedFile(null);
      setApiError('Please select an image file (JPG/PNG)');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setSelectedFile(null);
      setApiError('File too large (max 5MB)');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setSelectedFile(file);
    setApiError(null);
  };

  // Upload photo: block for school
  const handlePhotoUpload = async () => {
    if (!isEditable || viewMode === 'school') {
      showReadOnlyBlocked();
      return;
    }
    try {
      const isAlreadyCompleted = await fetchTreeStats();
      if (isAlreadyCompleted) {
        setShowPhotoUploadModal(false);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        showToast('Completed', 'Plantation already completed. Photo upload is disabled.', 'info');
        return;
      }

      setLoading(true);
      setApiError(null);
      if (!selectedFile) throw new Error('Please select a photo');
      const user = JSON.parse(localStorage.getItem('user'));
      const formData = new FormData();
      formData.append("user_id", user.id);
      formData.append("photo_url", selectedFile);

      // ✅ Get token from localStorage
      const token = getToken();
      if (!token) throw new Error('User not authenticated');

      const response = await bearerAuth("/upload-tree-photo", "POST", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${token}` // <-- send token here
        },
      });

      if (response.data?.status) {
        setShowPhotoUploadModal(false);
        setSelectedFile(null);
        setHasPlantationCompleted(true);
        if (fileInputRef.current) fileInputRef.current.value = '';
        Swal.fire({
          icon: 'success',
          title: 'Plantation completed',
          text: 'You can go over product and services page to explore there.',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Products',
          denyButtonText: 'Services',
          cancelButtonText: 'Later',
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.href = '/product';
          } else if (result.isDenied) {
            window.location.href = '/services';
          }
        });
      } else {
        throw new Error(response.data?.message || 'Upload failed');
      }
    } catch (err) {
      console.error('photo upload error:', err);
      setApiError(err.response?.data?.message || err.message || 'Upload failed');
      showToast('Error', err.message || 'Failed to upload photo', 'error');
    } finally {
      setLoading(false);
    }
  };

 const handleDownloadCertificate = () => {
  if (!isEligibleForCertificate()) {
    showToast('Certificate Not Available', 'Complete all classes and take at least one mock test to download certificate.', 'info');
    return;
  }
  if (!certificateRef.current) return;

  html2canvas(certificateRef.current, {
    scale: 2,
    logging: false,
    useCORS: true,
    backgroundColor: '#ffffff',
    width: 1123,
    height: 794,
    windowWidth: 1123,
    windowHeight: 794,
  }).then((canvas) => {
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('l', 'mm', [297, 210]); // landscape A4
    pdf.addImage(imgData, 'PNG', 0, 0, 297, 210);
    const filename = `${(userData?.username || 'student').replace(/\s+/g, '_')}_certificate.pdf`;
    pdf.save(filename);
    showToast('Success', 'Certificate downloaded successfully!');
  }).catch((err) => {
    console.error('certificate export error:', err);
    showToast('Error', 'Failed to download certificate', 'error');
  });
};
  const getStatusBadge = (date) => {
    if (isCompleted(date)) return <Badge bg="success"><FaCheckCircle className="me-1" /> Completed</Badge>;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const dateOnly = new Date(date); dateOnly.setHours(0, 0, 0, 0);
    if (dateOnly < today) return <Badge bg="danger"><FaTimesCircle className="me-1" /> Pending</Badge>;
    return <Badge bg="primary"><FaCalendarAlt className="me-1" /> Upcoming</Badge>;
  };

  const EmojiRating = () => {
    const emojis = ['😞', '😕', '😐', '😊', '😄'];
    return (
      <div className="mb-3">
        <Form.Label>Rate your class experience</Form.Label>
        <div className="d-flex">
          {emojis.map((emoji, i) => {
            const val = i + 1;
            return (
              <Button
                key={val}
                variant="link"
                className="p-0 mx-1"
                style={{ fontSize: 24, color: val <= (hover || rating) ? '#ffc107' : '#e4e5e9', background: 'none', border: 'none' }}
                onClick={() => {
                  if (!isEditable || viewMode === 'school') {
                    showReadOnlyBlocked();
                    return;
                  }
                  setRating(val);
                }}
                onMouseEnter={() => setHover(val)}
                onMouseLeave={() => setHover(rating)}
              >
                {emoji}
              </Button>
            );
          })}
        </div>
        <div className="small text-muted">{rating === 0 ? 'Select your rating' : `You rated ${rating}`}</div>
      </div>
    );
  };

  if (initialLoading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading schedule data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <Alert variant="danger">
          <Alert.Heading>Error loading schedule</Alert.Heading>
          <p>{error}</p>
          <Button variant="primary" onClick={fetchUserData}>Try Again</Button>
        </Alert>
      </div>
    );
  }

  const progressPercentage = calculateProgress();
  const progressColor = getProgressColor();
  const progressText = getProgressText();
  const isCertificateEligible = isEligibleForCertificate();
  const schoolName = userData?.driving_school_name || user?.driving_school_name || userData?.school_name || 'Driving School';
  const schoolAddress = userData?.driving_school_address || user?.driving_school_address || '';
  const addressParts = String(schoolAddress)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const schoolCity = userData?.city_name || user?.city_name || addressParts[addressParts.length - 3] || 'City';
  const schoolCountry = addressParts[addressParts.length - 1] || 'India';
  const schoolLocation = `${schoolCity}, ${schoolCountry}`;
  const completionDateText = getCompletionDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const fromLineText = `${schoolName} • ${schoolLocation} • Completed on ${completionDateText}`;
  const studentUsername = userData?.username || user?.username || 'username';
  const courseName = userData?.CourseName || 'Course Name';
  const certificateLogo = `${process.env.PUBLIC_URL || ''}/assets/images/logonew1.png`;
  const certificateTemplate = `${process.env.PUBLIC_URL || ''}/assets/images/certificate.png`;
  const mockTotalQuestions = mockTestResults.reduce((sum, r) => sum + (Number(r.total_questions) || 0), 0);
  const mockTotalScore = mockTestResults.reduce((sum, r) => sum + (Number(r.score) || 0), 0);
  const mockProgress = mockTotalQuestions > 0 ? Math.round((mockTotalScore / mockTotalQuestions) * 100) : 0;

  return (
    <div className="container py-4">
      {/* Certificate download with eligibility check */}
      {hasCompletedAllClasses && (
        <div className="d-flex justify-content-end flex-wrap gap-2 mb-3">
          <Button
            variant={hasPlantationCompleted ? "outline-success" : "success"}
            onClick={() => setShowTreeModal(true)}
            className="d-inline-flex align-items-center"
          >
            <FaTree className="me-2" />
            {hasPlantationCompleted ? "Plantation Completed" : "Plantation Tree"}
          </Button>
          <Button
            variant={isCertificateEligible ? "success" : "secondary"}
            onClick={handleCertificateClick}
            className="d-inline-flex align-items-center"
          >
            <FaDownload className="me-2" />
            {isCertificateEligible ? "Download Certificate" : "Certificate Not Available"}
          </Button>
          {!isCertificateEligible && (
            <div className="w-100 text-end">
              <small className="text-muted">
                Complete all classes and take at least one mock test to unlock certificate.
              </small>
            </div>
          )}
        </div>
      )}

      {/* Progress Bar Section */}
      {scheduleDates.length > 0 && (
        <Card className="shadow mb-4 border-0">
          <Card.Body className="bg-light rounded">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0 text-dark">Course Progress</h5>
              <Badge bg={progressColor} className="fs-6">
                {progressPercentage}%
              </Badge>
            </div>
            <ProgressBar 
              now={progressPercentage} 
              variant={progressColor} 
              animated={progressPercentage < 100}
              className="mb-2"
              style={{ height: '12px' }}
            />
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">{progressText}</small>
              <small className="text-muted">
                {localCompleted.length} of {scheduleDates.length} classes completed
              </small>
            </div>
            
            {/* Mock Test Status */}
            <div className="mt-3 pt-3 border-top">
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  <FaFileAlt className="me-1" />
                  Mock Tests: {hasTakenMockTest ? 'Completed' : 'Pending'}
                </small>
                <Badge bg={hasTakenMockTest ? "success" : "warning"}>
                  {mockTestResults.length} taken
                </Badge>
              </div>
              <div className="d-flex justify-content-between align-items-center mt-2">
                <small className="text-muted">
                  Questions: {mockTotalQuestions > 0 ? `${mockTotalScore}/${mockTotalQuestions}` : 'N/A'}
                </small>
                <Badge bg={mockTotalQuestions > 0 ? "info" : "secondary"}>
                  {mockTotalQuestions > 0 ? `${mockProgress}%` : 'No data'}
                </Badge>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      <Card className="shadow mb-4">
        <Card.Header className="bg-primary text-white">
          <h2 className="mb-0 d-flex align-items-center"><FaCalendarAlt className="me-2" /> Class Schedule</h2>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

         <div 
  className="table-responsive"
  style={{
    maxHeight: '400px',   // yahan height adjust kar sakte ho
    overflowY: 'auto'
  }}
>
  <Table striped bordered hover className="mb-0">

              <thead className="table-dark">
                <tr>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Status</th>
                  <th className="text-end">Have you completed your class?</th>
                </tr>
              </thead>
              <tbody>
                {scheduleDates.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4">{userData?.admin_exact_start_at ? 'Generating schedule...' : 'No schedule data available'}</td>
                  </tr>
                ) : (
                  scheduleDates.map((date, idx) => {
                    const completed = isCompleted(date);
                    const eligible = isEligibleForCompletion(date);

                    return (
                      <tr key={idx}>
                        <td className="align-middle"><strong>{formatDate(date)}</strong></td>
                        <td className="align-middle">{date.toLocaleDateString('en-US', { weekday: 'long' })}</td>
                        <td className="align-middle">{getStatusBadge(date)}</td>
                        <td className="align-middle text-end">
                          {eligible && !completed ? (
                            <div className="d-flex flex-column align-items-end">
                              <p className="mb-2 small text-muted">Have you completed your class?</p>
                              {/* If school view, show disabled button and info */}
                              {(!isEditable || viewMode === 'school') ? (
                                <Button variant="outline-secondary" size="sm" disabled title="Read-only">
                                  Yes
                                </Button>
                              ) : (
                                <Button variant="success" size="sm" onClick={() => handleCompleteClass(date)}>
                                  <FaCheckCircle className="me-1" /> Yes
                                </Button>
                              )}
                            </div>
                          ) : (
                            <div className="text-muted small">{completed ? 'Completed' : 'Not applicable'}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
        <Card.Footer className="text-muted">
          Showing {scheduleDates.length} scheduled classes {userData?.inc_sat_sun !== 1 && '(excluding weekends)'}
          {mockTestResults.length > 0 && (
            <div className="mt-2">
              <Badge bg="info" className="me-2">
                <FaFileAlt className="me-1" />
                Mock Tests Taken: {mockTestResults.length}
              </Badge>
            </div>
          )}
        </Card.Footer>
      </Card>

      {/* Mock Test Alert Modal */}
      <Modal show={showMockTestAlert} onHide={() => setShowMockTestAlert(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title><FaFileAlt className="me-2 text-warning" /> Certificate Not Available</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <FaFileAlt className="text-warning mb-3" style={{ fontSize: '3rem' }} />
            <h5>Complete Requirements for Certificate</h5>
            <p className="text-muted">
              To download your certificate, you need to complete the following:
            </p>
            <div className="text-start">
              <ul>
                <li className={completedClasses.length === scheduleDates.length ? 'text-success' : 'text-muted'}>
                  ✓ Complete all {scheduleDates.length} classes ({completedClasses.length}/{scheduleDates.length} completed)
                </li>
                <li className={hasTakenMockTest ? 'text-success' : 'text-muted'}>
                  {hasTakenMockTest ? '✓' : '✗'} Take at least one mock test ({mockTestResults.length} taken)
                </li>
              </ul>
            </div>
            {!hasTakenMockTest && (
              <Alert variant="info" className="mt-3">
                <strong>Note:</strong> Please complete mock tests to unlock your certificate.
              </Alert>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowMockTestAlert(false)}>
            Understand
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Completion Modal */}
      <Modal show={showCompletionModal} onHide={() => !loading && setShowCompletionModal(false)} centered>
        <Modal.Header closeButton className={loading ? 'bg-light pe-none' : ''}>
          <Modal.Title><FaCheckCircle className="me-2 text-success" /> Mark Class as Completed - {selectedDate && formatDate(selectedDate)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" variant="primary" /><p className="mt-3 mb-0">Submitting your completion...</p></div>
          ) : (
            <>
              {apiError && <Alert variant="danger" className="mb-3">{apiError}</Alert>}
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="completed">Completed</option>
                  </Form.Select>
                </Form.Group>

                <EmojiRating />

                <Form.Group className="mb-3">
                  <Form.Label>Remarks (optional)</Form.Label>
                  <Form.Control as="textarea" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Any notes about this class..." style={{ resize: 'none' }} />
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className={loading ? 'bg-light pe-none' : ''}>
          <Button variant="secondary" onClick={() => setShowCompletionModal(false)} disabled={loading}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmitCompletion} disabled={loading || rating === 0 || !isEditable || viewMode === 'school'}>
            {loading ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Submitting...</> : 'Confirm Completion'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Tree Plantation Modal */}
      <Modal show={showTreeModal} onHide={() => !loading && setShowTreeModal(false)} centered>
        <Modal.Header closeButton className={loading ? 'bg-light pe-none' : ''}>
          <Modal.Title><FaTree className="me-2 text-success" /> Congratulations!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? <div className="text-center py-4"><Spinner animation="border" /><p className="mt-3 mb-0">Processing...</p></div> : <>
            {apiError && <Alert variant="danger" className="mb-3">{apiError}</Alert>}
            <p className="fs-5">Congratulations, you are ready to give mock test for download certificate.</p>
            <p className="text-muted mb-0">Choose plantation tree first to continue.</p>
          </>}
        </Modal.Body>
        <Modal.Footer className={loading ? 'bg-light pe-none' : ''}>
          <Button variant="secondary" onClick={() => setShowTreeModal(false)} disabled={loading}>No</Button>
          <Button variant="success" onClick={handleTreePlantationOptIn} disabled={loading || !isEditable || viewMode === 'school'}>
            {loading ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Processing...</> : 'Plantation Tree'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Planting Method Selection Modal */}
      <Modal show={showPlantingMethodModal} onHide={() => !loading && setShowPlantingMethodModal(false)} centered>
        <Modal.Header closeButton className={loading ? 'bg-light pe-none' : ''}>
          <Modal.Title><FaTree className="me-2 text-success" /> Choose Planting Method</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? <div className="text-center py-4"><Spinner animation="border" /><p className="mt-3 mb-0">Processing...</p></div> : <>
            {apiError && <Alert variant="danger" className="mb-3">{apiError}</Alert>}
            <p className="fs-5 mb-4">How would you like to plant your tree?</p>
            
            <div className="row g-3">
              <div className="col-md-6">
                <div 
                  className="card h-100 text-center border-2 planting-method-card"
                  style={{ cursor: 'pointer', borderColor: plantingMethod === 'self' ? '#198754' : '#dee2e6' }}
                  onClick={() => handlePlantingMethodSelect('self')}
                >
                  <div className="card-body">
                    <FaUser className="text-primary mb-3" style={{ fontSize: '3rem' }} />
                    <h5 className="card-title">Plant by Self</h5>
                    <p className="card-text text-muted">You will plant the tree yourself and upload a photo as proof.</p>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div 
                  className="card h-100 text-center border-2 planting-method-card"
                  style={{ cursor: 'pointer', borderColor: plantingMethod === 'asysta' ? '#198754' : '#dee2e6' }}
                  onClick={() => handlePlantingMethodSelect('asysta')}
                >
                  <div className="card-body">
                    <FaHandsHelping className="text-success mb-3" style={{ fontSize: '3rem' }} />
                    <h5 className="card-title">Plant by ASYSTA</h5>
                    <p className="card-text text-muted">ASYSTA team will plant the tree on your behalf.</p>
                  </div>
                </div>
              </div>
            </div>
          </>}
        </Modal.Body>
        <Modal.Footer className={loading ? 'bg-light pe-none' : ''}>
          <Button variant="secondary" onClick={() => setShowPlantingMethodModal(false)} disabled={loading}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Photo Upload Modal */}
      <Modal show={showPhotoUploadModal} onHide={() => !loading && setShowPhotoUploadModal(false)} centered>
        <Modal.Header closeButton className={loading ? 'bg-light pe-none' : ''}>
          <Modal.Title><FaTree className="me-2 text-success" /> Upload Tree Photo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? <div className="text-center py-4"><Spinner animation="border" /><p className="mt-3 mb-0">Uploading photo...</p></div> : <>
            {hasPlantationCompleted && treeStatsChecked && (
              <Alert variant="success" className="mb-3">
                Plantation completed. Photo upload is disabled.
              </Alert>
            )}
            {apiError && <Alert variant="danger" className="mb-3">{apiError}</Alert>}
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Select Tree Photo</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  disabled={hasPlantationCompleted && treeStatsChecked}
                />
                <Form.Text className="text-muted">Please upload a photo of the tree you planted (JPG, PNG) up to 5MB.</Form.Text>
              </Form.Group>
            </Form>
          </>}
        </Modal.Body>
        <Modal.Footer className={loading ? 'bg-light pe-none' : ''}>
          <Button variant="secondary" onClick={() => { setShowPhotoUploadModal(false); setSelectedFile(null); setApiError(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} disabled={loading}>Cancel</Button>
          <Button variant="primary" onClick={handlePhotoUpload} disabled={loading || !selectedFile || !isEditable || viewMode === 'school' || (hasPlantationCompleted && treeStatsChecked)}>
            {loading ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Uploading...</> : 'Upload Photo'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Certificate Modal */}
      {/* Certificate Modal */}
<Modal show={showCertificateModal} onHide={() => setShowCertificateModal(false)} 
  size="xl" centered backdrop="static" 
  dialogClassName="modal-90w"
>
  <Modal.Header closeButton className="border-0">
    <Modal.Title className="fw-bold">Your Driving Certificate</Modal.Title>
  </Modal.Header>
  <Modal.Body className="text-center p-2" style={{ overflowX: 'auto' }}>
    <div
  ref={certificateRef}
  style={{
    width: '1123px',
    height: '794px',
    backgroundImage: `url(${certificateTemplate})`,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
    position: 'relative',
    overflow: 'hidden',
    margin: '0 auto',
    flexShrink: 0,
  }}
>
  {/* Logo */}
  <img
    src={certificateLogo}
    alt="ASYSTA Logo"
    style={{
      position: 'absolute',
      top: '30px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '108px',
      objectFit: 'contain',
      zIndex: 5,
    }}
    onError={(e) => { e.target.style.display = 'none'; }}
  />

  {/* Student Name — "This Certifies that" ke baad wali blank line pe exactly */}
  <div style={{
    position: 'absolute',
    top: '310px',
    left: '505px',
    zIndex: 6,
    color: '#1d3272',
    fontWeight: 700,
    fontSize: '20px',
    fontFamily: '"Trebuchet MS", Arial, sans-serif',
    whiteSpace: 'nowrap',
    lineHeight: 1,
  }}>
    {userData?.full_name || userData?.username || 'STUDENT NAME'}
  </div>

  {/* School • City, Country — "Completed the Driving Course From" ke niche wali blank line pe */}
  <div style={{
    position: 'absolute',
    top: '408px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 6,
    color: '#262525',
    fontSize: '18px',
    fontFamily: '"Trebuchet MS", Arial, sans-serif',
    fontWeight: 400,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    lineHeight: 1,
  }}>
    {schoolName} • {schoolCity}, {schoolCountry}
  </div>

  {/* Completion Date — "Date of Completion:" ke baad wali line ke bilkul upar */}
  <div style={{
    position: 'absolute',
    top: '545px',
    left: '572px',
    zIndex: 6,
    color: '#2e2e2e',
    fontSize: '18px',
    fontFamily: '"Trebuchet MS", Arial, sans-serif',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    lineHeight: 1,
  }}>
    {completionDateText}
  </div>

  {/* Student Code value (username) — "Student Code: ___" line ke bilkul upar */}
  <div style={{
    position: 'absolute',
    top: '609px',
    left: '283px',
    zIndex: 6,
    color: '#2e2e2e',
    fontSize: '18px',
    fontFamily: '"Trebuchet MS", Arial, sans-serif',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    lineHeight: 1,
  }}>
    {studentUsername}
  </div>

  {/* Course Name value — "Course Name: ___" line ke bilkul upar */}
  <div style={{
    position: 'absolute',
    top: '640px',
    left: '245px',
    zIndex: 6,
    color: '#474747',
    fontSize: '18px',
    fontFamily: '"Trebuchet MS", Arial, sans-serif',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    lineHeight: 1,
  }}>
    {courseName}
  </div>

  {/* School Name value — "School Name: ___" line ke bilkul upar (right side) */}
  <div style={{
    position: 'absolute',
    top: '613px',
    left: '799px',
    zIndex: 6,
    color: '#2f2f2f',
    fontSize: '18px',
    fontFamily: '"Trebuchet MS", Arial, sans-serif',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    lineHeight: 1,
  }}>
    {schoolName}
  </div>
</div>
  </Modal.Body>
  <Modal.Footer className="border-0 justify-content-center">
    <Button variant="secondary" onClick={() => setShowCertificateModal(false)} className="me-3">Close</Button>
    {!hasPlantationCompleted && (
      <Button variant="outline-success" onClick={() => setShowTreeModal(true)} className="d-flex align-items-center">
        <FaTree className="me-2" /> Plantation Tree
      </Button>
    )}
    <Button variant="primary" onClick={handleDownloadCertificate} className="d-flex align-items-center">
      <FaDownload className="me-2" /> Download Certificate
    </Button>
  </Modal.Footer>
</Modal>
    </div>
  );
};

export default Schedule;
